import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import { configAreaSchema } from '../../interfaces/IConfigArea';
import { AreaTypeEnum } from '../../interfaces/AreaType.enum';
import { coordinateSchema } from '../../interfaces/ICoordinate';

// ============================================================
// Param Schemas
// ============================================================

const projectAreaParamsSchema = z.object({
	projectId: requiredStringSchema,
	areaId: requiredStringSchema.describe('Area ID'),
});

// ============================================================
// Query Schemas
// ============================================================

/**
 * All four are filters on the collection. `areaType` used to have its own route
 * (`GET /area/byAreaType`) — a filtered collection wearing a nested-resource
 * costume, which is the shape the playbook says not to replicate.
 */
const getAreasQuerySchema = z.object({
	floorId: z.string().optional(),
	floorIds: z.string().optional(),
	areaIds: z.string().optional(),
	areaType: z.enum(AreaTypeEnum).optional(),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * Elements carry no `projectId`. They used to, and an array body exposes no
 * top-level key to the membership guard — this route was marked GUARD-BLIND.
 */
const insertAreasBodySchema = z.array(
	z.object({
		floorId: requiredStringSchema,
		areaId: requiredStringSchema,
		areaNick: requiredStringSchema,
		areaType: z.enum(AreaTypeEnum).optional(),
		areaSortIndex: z.number().optional(),
		points: z.array(coordinateSchema).optional(),
		centrePoint: coordinateSchema.optional(),
	})
);

const applyTemplateBodySchema = z.object({
	sourceFloorId: requiredStringSchema,
	targetFloorIds: z.array(z.string()),
});

const updateAreaBodySchema = z.object({
	areaType: z.string().optional(),
	areaNick: z.string().optional(),
	areaSortIndex: z.number().nullable().optional(),
});

const updateAreaCentrePointBodySchema = z.object({
	centrePoint: coordinateSchema,
});

const deleteAreasByIdsBodySchema = z.object({
	ids: z.array(z.string()),
});

// ============================================================
// Contract
// ============================================================

/**
 * Areas are project data: `/projects/:projectId/areas`.
 *
 * `{ root: true }` — one route in this file is NOT project data, `getAreaTypes`,
 * and it therefore spells its old `/area/areaTypes` path literally. Without that
 * it would silently start serving `/api/projectConfig/areaTypes`.
 *
 * IMPORTANT: literal sub-paths (`/types`, `/apply-template`, `/byAreaIds`) must
 * be declared BEFORE the parameterized `/:areaId`.
 */
export const areaContract = defineContract(
	'area',
	{
		getAreas: route({
			method: 'GET',
			path: '/projects/:projectId/areas',
			params: projectIdParamsSchema,
			query: getAreasQuerySchema,
			response: z.array(configAreaSchema),
			operationId: 'getAreas',
			tags: ['Area'],
			summary: 'List the areas of a project, optionally filtered',
		}),
		getProjectAreasTypes: route({
			method: 'GET',
			path: '/projects/:projectId/areas/types',
			params: projectIdParamsSchema,
			response: z.array(z.string()),
			operationId: 'getProjectAreasTypes',
			tags: ['Area'],
			summary: 'List the area types actually used in a project',
		}),
		insertAreas: route({
			method: 'POST',
			path: '/projects/:projectId/areas',
			params: projectIdParamsSchema,
			body: insertAreasBodySchema,
			response: z.string(),
			operationId: 'insertAreas',
			tags: ['Area'],
			summary: 'Insert areas into a project',
		}),
		applyTemplate: route({
			method: 'POST',
			path: '/projects/:projectId/areas/apply-template',
			params: projectIdParamsSchema,
			body: applyTemplateBodySchema,
			response: z.any(),
			operationId: 'applyAreaTemplate',
			tags: ['Area'],
			summary: 'Copy a floor of areas onto other floors of the same project',
		}),
		/**
		 * NOT project data — a static enumeration of the `AreaTypeEnum` values,
		 * with no database and no tenant dimension. It keeps its literal
		 * `/area/areaTypes` path because `{ root: true }` would otherwise move it,
		 * and it is classified GLOBAL ADMIN in `PROJECT_LESS_BY_DESIGN`.
		 */
		getAreaTypes: route({
			method: 'GET',
			path: '/area/areaTypes',
			response: z.array(z.string()),
			operationId: 'getAreaTypes',
			tags: ['Area'],
			summary: 'List every area type the system defines',
		}),
		deleteAreas: route({
			method: 'DELETE',
			path: '/projects/:projectId/areas',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'deleteAreas',
			tags: ['Area'],
			summary: 'Delete every area of a project',
		}),
		deleteAreasByIds: route({
			method: 'DELETE',
			path: '/projects/:projectId/areas/byAreaIds',
			params: projectIdParamsSchema,
			body: deleteAreasByIdsBodySchema,
			response: z.string(),
			operationId: 'deleteAreasByIds',
			tags: ['Area'],
			summary: 'Delete areas of a project by their areaIds',
		}),
		getArea: route({
			method: 'GET',
			path: '/projects/:projectId/areas/:areaId',
			params: projectAreaParamsSchema,
			response: configAreaSchema,
			operationId: 'getArea',
			tags: ['Area'],
			summary: 'Get an area of a project by areaId',
		}),
		updateArea: route({
			method: 'PUT',
			path: '/projects/:projectId/areas/:areaId',
			params: projectAreaParamsSchema,
			body: updateAreaBodySchema,
			response: z.string(),
			operationId: 'updateArea',
			tags: ['Area'],
			summary: 'Update an area of a project',
		}),
		updateAreaCentrePoint: route({
			method: 'PUT',
			path: '/projects/:projectId/areas/:areaId/centrePoint',
			params: projectAreaParamsSchema,
			body: updateAreaCentrePointBodySchema,
			response: z.string(),
			operationId: 'updateAreaCentrePoint',
			tags: ['Area'],
			summary: 'Update the centre point of an area of a project',
		}),
	},
	{ root: true }
);
